// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/financeiro/services/conciliacaoOfxService.ts
//  Service para import OFX e conciliação bancária.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { PaginadoResponse } from '@/features/financeiro/types';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ImportOfxResultado {
  importOfxId: number;
  qtdImportados: number;
  qtdDuplicados: number;
  dtInicio: string;
  dtFim: string;
  saldoFinal: number | null;
}

export interface MovimentoBancario {
  id: number;
  dtMovimento: string;
  valor: number;
  tipoOfx: string | null;
  memo: string | null;
  checkNum: string | null;
  fitId: string;
  isConciliado: boolean;
  conciliadoComTipo: 'CR' | 'CP' | null;
  conciliadoComId: number | null;
  dtConciliacao: string | null;
}

export interface SugestaoCasamento {
  movimentoBancarioId: number;
  dtMovimento: string;
  valor: number;
  memo: string | null;
  checkNum: string | null;
  tipoSugerido: 'CR' | 'CP';
  contaSugeridaId: number;
  contaSugeridaDoc: string;
  contaSugeridaPessoa: string;
  contaSugeridaVencimento: string;
  contaSugeridaValor: number;
  scoreCasamento: number;
}

export interface ImportOfxRegistro {
  id: number;
  nomeArquivo: string | null;
  dtInicio: string;
  dtFim: string;
  qtdMovimentos: number;
  qtdDuplicados: number;
  saldoFinal: number | null;
  dtImport: string;
}

// ── Service ──────────────────────────────────────────────────────────────────

export const conciliacaoOfxService = {
  async importar(contaCorrenteId: number, arquivo: File): Promise<ImportOfxResultado> {
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    const { data } = await apiClient.post<ImportOfxResultado>(
      `/api/conciliacao-ofx/${contaCorrenteId}/importar`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 },
    );
    return data;
  },

  async listarMovimentos(contaCorrenteId: number, params?: {
    dtInicio?: string; dtFim?: string; conciliado?: boolean;
    pagina?: number; tamanhoPagina?: number;
  }): Promise<PaginadoResponse<MovimentoBancario>> {
    const { data } = await apiClient.get<PaginadoResponse<MovimentoBancario>>(
      `/api/conciliacao-ofx/${contaCorrenteId}/movimentos`, { params });
    return data;
  },

  async sugestoes(contaCorrenteId: number): Promise<SugestaoCasamento[]> {
    const { data } = await apiClient.get<SugestaoCasamento[]>(
      `/api/conciliacao-ofx/${contaCorrenteId}/sugestoes`);
    return data;
  },

  async casar(movimentoId: number, tipo: 'CR' | 'CP', contaId: number): Promise<void> {
    await apiClient.post(`/api/conciliacao-ofx/casar/${movimentoId}`, { tipo, contaId });
  },

  async desfazer(movimentoId: number): Promise<void> {
    await apiClient.post(`/api/conciliacao-ofx/desfazer/${movimentoId}`);
  },

  async historicoImports(contaCorrenteId: number): Promise<ImportOfxRegistro[]> {
    const { data } = await apiClient.get<ImportOfxRegistro[]>(
      `/api/conciliacao-ofx/${contaCorrenteId}/imports`);
    return data;
  },
};
